/**
 * Purpose: Defines the TypeScript interface for community branding settings.
 * Location: src/lib/types/branding.ts
 * Constraints: Must accurately reflect the JSONB structure returned by the
 *              `get_community_branding_by_host` SQL function, including the default structure.
 */

import type { JobBoardConfiguration } from './jobBoardSettings'; // Import JobBoardConfiguration
import type {
  ColorPalette,
  AppearanceSettingsData as CoreAppearanceSettings,
} from './theme'; // Using CoreAppearanceSettings
import type { NavigationSettings } from './navigation'; // Import NavigationSettings
import { DEFAULT_NAVIGATION_SETTINGS } from './navigation'; // Import default navigation
import { DEFAULT_THEME } from '../constants/themes'; // For default palette

export interface BrandingColors {
  primary: string;
  secondary: string;
  accent: string;
  textOnPrimary: string;
  textOnSecondary: string;
  background: string;
  cardBackground: string;
  textDefault: string;
  textMuted: string;
}

export interface BrandingFont {
  primary: string; // e.g., "Inter, sans-serif"
  // secondary?: string; // Optional: for headings or specific elements
}

export interface BrandingLoginPage {
  backgroundImageUrl?: string | null;
  title: string;
  message: string;
  buttonText: string;
  backgroundColor: string;
  textColor: string;
}

export interface BrandingFooterLink {
  text: string;
  url: string;
}

export interface BrandingFooter {
  copyrightText: string; // Can use {year} and {communityName} placeholders
  links: BrandingFooterLink[];
  backgroundColor: string;
  textColor: string;
}

export interface BrandingSettings {
  theme: 'light' | 'dark';
  colors: BrandingColors;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  font: BrandingFont;
  loginPage: BrandingLoginPage;
  footer: BrandingFooter;

  // New fields for community core info and privacy
  communityId?: string;
  communityName?: string;
  communitySlug?: string;
  jobBoardPubliclyAccessible?: boolean;
  memberTypeNameSingular?: string;
  jobBoardSettings?: JobBoardConfiguration | null; // Added job board settings

  // Add other branding aspects here as needed, e.g.:
  // customCss?: string;
  // announcementBanner?: {
  //   text: string;
  //   backgroundColor: string;
  //   textColor: string;
  //   isActive: boolean;
  // };
}

/**
 * Default branding settings, mirroring the SQL function's default.
 * Useful for initial states or fallbacks in the frontend.
 */
export const defaultBrandingSettings: BrandingSettings = {
  theme: 'light',
  colors: {
    primary: '#5E5ADB', // A nice default purple
    secondary: '#E5E7EB', // Light gray
    accent: '#10B981', // Green
    textOnPrimary: '#FFFFFF',
    textOnSecondary: '#1F2937',
    background: '#F9FAFB', // Very light gray
    cardBackground: '#FFFFFF', // White
    textDefault: '#111827', // Almost black
    textMuted: '#6B7280', // Medium gray
  },
  logoUrl: null,
  faviconUrl: null,
  font: {
    primary: 'Inter, sans-serif',
  },
  loginPage: {
    backgroundImageUrl: null,
    title: 'Welcome to Terrarium',
    message: 'Connect and grow with your community.',
    buttonText: 'Sign In',
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
  },
  footer: {
    copyrightText: '&copy; {year} Terrarium',
    links: [],
    backgroundColor: '#E5E7EB',
    textColor: '#1F2937',
  },
  // Defaults for new fields
  communityId: undefined,
  communityName: 'Our Community', // Generic default
  communitySlug: undefined,
  jobBoardPubliclyAccessible: false, // Default to private for safety
  memberTypeNameSingular: 'Member', // Generic default
  jobBoardSettings: null, // Default for job board settings
};

/**
 * Represents the raw data structure returned by the
 * `get_community_branding_for_client` RPC function.
 */
export interface ClientRpcBrandingPayload {
  communityId: string | null;
  communityName: string;
  communitySlug: string | null;
  jobBoardPubliclyAccessible?: boolean;
  memberTypeNameSingular?: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  bannerUrl: string | null;
  appearance: CoreAppearanceSettings | null; // Contains activeThemeId, customPalette, etc.
  navigation: NavigationSettings | null; // Navigation settings from community.settings.navigation
  typography?: BrandTypography | null; // Typography settings including fonts
  loginPage: {
    title?: string;
    subtitle?: string;
    welcomeMessage?: string;
    buttonText?: string;
    backgroundColor?: string;
    textColor?: string;
    sideImageUrl?: string | null;
  } | null;
  jobBoardSettings: JobBoardConfiguration | null;
}

/**
 * Represents the branding settings as passed to component props
 * (mostly used by withCommunityAccessControl HOC)
 */
export interface BrandingSettings {
  communityId?: string | null;
  communityName?: string;
  communitySlug?: string | null;
  jobBoardPubliclyAccessible?: boolean;
  memberTypeNameSingular?: string;
  logoUrl?: string | null;
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  [key: string]: any; // Allow additional properties
}

/**
 * Represents the fully processed and resolved branding settings
 * that the useBranding hook will provide to the application and set in Jotai state.
 */
export interface ResolvedBrandingUI {
  communityId: string | null;
  communityName: string;
  communitySlug: string | null;
  jobBoardPubliclyAccessible?: boolean;
  memberTypeNameSingular?: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  bannerUrl: string | null;
  loginPage: ClientRpcBrandingPayload['loginPage'];
  navigation: NavigationSettings | null; // Navigation settings from community
  jobBoardSettings?: JobBoardConfiguration | null; // Job board configuration
  typography?: BrandTypography | null; // Typography settings

  // Resolved theme properties
  activeThemeId: string; // The ID of the theme ultimately applied (e.g., 'terrarium_orange_light', 'custom')
  isDark: boolean; // Whether the applied theme is a dark theme
  palette: ColorPalette; // The full, resolved color palette to be applied

  // Convenience property for backward compatibility
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };

  // Future resolved properties for fonts, roundness etc. can be added here
  // resolvedFonts?: { headingFontFamily?: string; bodyFontFamily?: string; };
  // resolvedElementRoundness?: { buttons?: string; cards?: string; inputs?: string; };

  jobBoardSettings: JobBoardConfiguration | null;
}

/**
 * Default branding settings, structured as ResolvedBrandingUI.
 * This will be used as a fallback by useBranding.
 */
export const defaultResolvedBrandingUI: ResolvedBrandingUI = {
  communityId: null,
  communityName: 'Terrarium',
  communitySlug: null,
  logoUrl: null,
  faviconUrl: null,
  bannerUrl: null,
  loginPage: {
    title: 'Welcome',
    subtitle: 'Please sign in',
    buttonText: 'Sign In',
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
  },
  navigation: DEFAULT_NAVIGATION_SETTINGS,
  activeThemeId: DEFAULT_THEME.id,
  isDark: DEFAULT_THEME.isDark || false,
  palette: DEFAULT_THEME.palette,
  colors: {},
  jobBoardSettings: null,
};

// Re-export extended branding types for Brand.dev integration
export type {
  ExtendedBranding,
  ExtendedClientRpcBrandingPayload,
  ExtendedResolvedBrandingUI,
  BrandApplicationConfig,
  ApplyBrandDataResult,
  BrandColors,
  BrandFont,
  BrandTypography,
  BrandLogos,
  BrandSocial,
  BrandImages,
  BrandMetadata,
  BrandSource,
  NamedColor,
  LogoVariant,
  EmployerBrandData,
} from './branding-extended';

export {
  isExtendedBranding,
  hasCustomFonts,
  hasSocialLinks,
  hasMultipleLogos,
} from './branding-extended';
